import {
  Component,
  DestroyRef,
  effect,
  inject,
  Injector,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { TimedMarkerMapMenuPanelComponent } from '@shared/components/timed-marker-map-menu-panel/timed-marker-map-menu-panel.component';
import { RegistryComponentNames } from '@shared/constants/registry-component-name';
import { AutoZoomFitParams, MapInput } from '@features/map/interfaces';
import {
  BehaviorSubject,
  combineLatest,
  interval,
  map,
  Observable,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { AssetPopupContentComponent } from '@shared/components/asset-popup-content/asset-popup-content.component';
import { AssetsGridService } from './components/assets-grid/assets-grid.service';
import { AssetDto } from './components/interfaces';
import { AssetsMapService } from './services/assets-map.service';
import { environment } from '@env/environment';
import { AssetViewType } from './components/constants';
import { ActivatedRoute, Router } from '@angular/router';
import { MapSetting } from '@features/map/constants/constants';
import { VtSplitterService } from '@shared/services/grid/vt-splitter.service';
import {
  DvSplitterEventsService,
  DvSplitterResizableSide,
  DvSplitterStatus,
} from '@devinco/ui/dds/splitter';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-assets-page',
  templateUrl: './assets-page.component.html',
  styleUrls: ['./assets-page.component.scss', '../../styles/page.scss'],
  providers: [AssetsMapService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AssetsPageComponent implements OnInit {
  private api = inject(RestApiService);
  private mapService = inject(AssetsMapService);
  private assetsGridService = inject(AssetsGridService);
  private splitterService = inject(VtSplitterService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private splitterEvents = inject(DvSplitterEventsService);
  private destroyRef = inject(DestroyRef);

  canAddAsset$: Observable<boolean>;
  @ViewChild('menuPanelTemplate')
  menuPanelContent: TemplateRef<TimedMarkerMapMenuPanelComponent>;
  canSeeMap$: Observable<boolean>;
  resizableSide: typeof DvSplitterResizableSide = DvSplitterResizableSide;

  isTreeViewActive = signal<boolean>(false);
  activeView = signal<AssetViewType>(AssetViewType.RegularView);
  splitterStatus = signal<DvSplitterStatus>(DvSplitterStatus.Open);
  isResizableSideOpenable = signal<boolean>(true);
  disableMapBtn = signal<boolean>(false);

  readonly assetViewType: typeof AssetViewType = AssetViewType;
  readonly mapSetting = MapSetting.AssetsPageMap;
  private markersArray$ = new BehaviorSubject<MapInput[]>([]);
  private refreshSubscription: Subscription | undefined;
  private autoZoomFit$ = new BehaviorSubject<AutoZoomFitParams>({
    doAutoFit: false,
  });
  private toggleMarkerClustering$ = new BehaviorSubject<boolean>(true);

  constructor() {
    const injector = inject(Injector);

    // do not register element if it's already defined
    if (!customElements.get(RegistryComponentNames.AssetPopupContent)) {
      const VtAssetMapPopupElement = createCustomElement(
        AssetPopupContentComponent,
        {
          injector,
        }
      );
      // Register the custom element with the browser.
      customElements.define(
        RegistryComponentNames.AssetPopupContent,
        VtAssetMapPopupElement
      );
    }

    effect(() => {
      const isTreeViewActive = this.activeView() === AssetViewType.TreeView;

      if (this.isTreeViewActive() !== isTreeViewActive) {
        this.isTreeViewActive.set(isTreeViewActive);
      }
    });
  }

  ngOnInit(): void {
    this.setQueryParams();
    this.canAddAsset$ = combineLatest([
      this.permissionsService.hasPermissions([RolesAndPermissions.AssetEdit]),
      this.permissionsService.hasPermissions([
        RolesAndPermissions.AssetEditRestricted,
      ]),
    ]).pipe(
      take(1),
      map(([hasEdit, hasEditRestricted]) => !!hasEdit || !!hasEditRestricted)
    );

    this.canSeeMap$ = this.permissionsService.hasPermissions([
      RolesAndPermissions.AssetShowInMap,
    ]);
  }

  onSplitterStatusChange(splitterStatus: string): void {
    this.splitterStatus.set(splitterStatus);
  }

  onSplitterOpenableChange(isOpen: boolean): void {
    this.isResizableSideOpenable.set(isOpen);
  }

  onShowAllAssetsClick(): void {
    this.disableMapBtn.set(true);
    this.getAssets().subscribe(assets => {
      this.markersArray$.next(this.mapService.getMapMarkers(assets));
      this.mapService
        .showAssetsOnMap(
          this.markersArray$,
          this.menuPanelContent,
          this.autoZoomFit$,
          this.toggleMarkerClustering$
        )
        .closed.pipe(take(1))
        .subscribe(() => {
          this.disableMapBtn.set(false);
          this.refreshSubscription?.unsubscribe();
        });
    });
  }

  onAutoRefreshClicked(isAutoRefreshOn: boolean): void {
    if (isAutoRefreshOn) {
      this.refreshSubscription = interval(environment.mapRefreshInterval)
        .pipe(
          switchMap(() =>
            this.getAssets().pipe(
              tap(resp => {
                this.markersArray$.next(this.mapService.getMapMarkers(resp));
              })
            )
          ),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    } else {
      this.refreshSubscription?.unsubscribe();
    }
  }

  onAutoZoomFitClicked(isAutoZoomOn: boolean): void {
    this.autoZoomFit$.next({ doAutoFit: isAutoZoomOn });
  }

  onGroupMarkersClicked(isClusteringOn: boolean): void {
    this.toggleMarkerClustering$.next(isClusteringOn);
  }

  onCreateClick(): void {
    this.splitterEvents.isSplitterOpened$.next(true);
  }

  changeViewUrl(viewType: AssetViewType): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        view: viewType,
      },
      queryParamsHandling: 'merge',
    });
    this.changeView(viewType);
  }

  private getAssets(): Observable<AssetDto[]> {
    return this.api
      .getData({
        entity: Entity.Asset,
        fields: this.assetsGridService
          .getColDefs()
          .filter(colDef => colDef.field)
          .map(colDef => colDef.field!),
        filterModel: this.splitterService.agGrid.api.getFilterModel(),
        quickFilterText:
          this.splitterService.agGrid.context?.quickFilterText ||
          this.splitterService.agGrid.gridOptions?.context?.quickFilterText,
      })
      .pipe(map(resp => resp.data));
  }

  private setQueryParams(): void {
    this.route.queryParams.pipe(take(1)).subscribe(q => {
      if (q['view'] === AssetViewType.TreeView) {
        this.changeViewUrl(AssetViewType.TreeView);
      } else {
        this.changeViewUrl(AssetViewType.RegularView);
      }
    });
  }

  private changeView(assetViewType: AssetViewType): void {
    this.activeView.set(assetViewType);
  }
}
